'use client'

import ConfirmModal from '@/components/confirm-modal'
import type { StoreCollectionKind } from '@mediardex/contracts'
import {
    createStoreCollection,
    createStorePage,
    createStoreThemePreview,
    discardStoreThemeDraft,
    getStoreThemeEditor,
    getStoreThemeFieldOptions,
    listStoreCollections,
    listStorePages,
    listStoreThemeProducts,
    listStoreThemeVersions,
    prepareStoreThemeSwitch,
    publishStoreTheme,
    removeStoreCollection,
    removeStorePage,
    reorderStoreCollections,
    reorderStorePages,
    restoreStoreThemeVersion,
    saveStorePageDraft,
    saveStoreThemeDraft,
    setStoreCollectionProducts,
    updateStoreCollection,
    updateStorePage,
    uploadStoreThemeMedia,
    type StoreCollectionItem,
    type StoreCollectionMeta,
    type StorePageItem,
    type StorePageMeta,
    type StoreThemeEditorState,
    type StoreThemeProduct,
    type StoreThemeVersion,
} from '@/lib/api/commerce/store-themes'
import {
    THEME_PART_KEYS,
    THEME_PARTS,
    THEME_SIDEBAR_LAYOUTS,
    themePartFields,
    type ThemeContent,
    type ThemePageKey,
    type ThemePartKey,
    type ThemeSectionInstance,
} from '@mediardex/contracts'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    MdArrowBack,
    MdClose,
    MdDesktopWindows,
    MdHistory,
    MdOpenInNew,
    MdPhoneIphone,
    MdPublish,
    MdStyle,
    MdUndo,
    MdVisibility,
} from 'react-icons/md'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import CollectionList from './theme-editor/collection-list'
import CollectionSettings from './theme-editor/collection-settings'
import NewCollectionDialog from './theme-editor/new-collection-dialog'
import NewPageDialog from './theme-editor/new-page-dialog'
import PageList, { type ThemeFixedPageId } from './theme-editor/page-list'
import PageSettings from './theme-editor/page-settings'
import PartList from './theme-editor/part-list'
import SectionList, { type ThemeEditorSelection } from './theme-editor/section-list'
import { ThemeFieldGrid, type ThemeFieldOptions } from './theme-editor/theme-field'

type SaveStatus = 'saved' | 'pending' | 'saving' | 'error'

/** Espera depois da última edição para gravar o rascunho. */
const AUTOSAVE_DELAY_MS = 1000

/**
 * Página inicial, barra lateral, página de produto e página de coleção: as seções
 * moram no conteúdo do tema. As institucionais, em `StorePage`.
 */
const HOME_PAGE: ThemePageKey = 'home'
const SIDEBAR_PAGE: ThemePageKey = 'sidebar'
const FIXED_PAGE_KEYS: Record<ThemeFixedPageId, ThemePageKey> = {
    sidebar: 'sidebar',
    product: 'product',
    collection: 'collection',
}

function formatDateTime(value: string): string {
    return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

/** Janela simples no padrão do `ConfirmModal`, para listas (versões, galeria). */
function ThemeDialog({
    open,
    title,
    onClose,
    children,
}: {
    open: boolean
    title: string
    onClose: () => void
    children: React.ReactNode
}) {
    if (!open) return null
    return (
        <dialog className="modal modal-open z-50">
            <div className="modal-box !mx-4 !max-w-xl !p-0">
                <div className="flex items-center justify-between border-b border-base-200 p-4">
                    <h2 className="text-lg font-bold">{title}</h2>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
                        <MdClose className="text-lg" />
                    </button>
                </div>
                <div className="max-h-[60vh] overflow-y-auto p-4">{children}</div>
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </dialog>
    )
}

/**
 * Editor do tema da loja (docs/store-themes.md §7): seções à esquerda, a loja ao vivo
 * no centro e os campos à direita. Toda edição vai para a prévia na hora e para o
 * rascunho um segundo depois; a vitrine só muda ao publicar.
 */
export default function StoreThemePage() {
    const { storeId } = useParams<{ storeId: string }>()
    // `?theme=<slug>`: a Loja de temas manda experimentar outro tema (prepara a troca).
    const [searchParams] = useSearchParams()
    const requestedTheme = searchParams.get('theme')
    // Segmento escolhido na demonstração ("Ver com segmento de"): só vale para o rascunho novo.
    const requestedCatalog = searchParams.get('segment') || undefined
    const storeConfigId = Number(storeId)

    const [editor, setEditor] = useState<StoreThemeEditorState | null>(null)
    const [content, setContent] = useState<ThemeContent | null>(null)
    const [hasDraft, setHasDraft] = useState(false)
    const [loading, setLoading] = useState(true)
    const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved')
    const [selection, setSelection] = useState<ThemeEditorSelection>({ kind: 'settings' })
    const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop')
    const [previewUrl, setPreviewUrl] = useState('')
    const [fieldOptions, setFieldOptions] = useState<Omit<ThemeFieldOptions, 'uploadMedia'>>({
        widgets: [],
        categories: [],
        collections: [],
    })

    const [publishOpen, setPublishOpen] = useState(false)
    const [discardOpen, setDiscardOpen] = useState(false)
    const [busy, setBusy] = useState(false)
    const [versionsOpen, setVersionsOpen] = useState(false)
    const [versions, setVersions] = useState<StoreThemeVersion[]>([])
    const [previewOpen, setPreviewOpen] = useState(false)

    // Páginas: a lista das institucionais, a aberta (null = inicial; 'sidebar',
    // 'product' e 'collection' = páginas fixas do tema) e as seções em edição.
    const [pages, setPages] = useState<StorePageItem[]>([])
    const [currentPageId, setCurrentPageId] = useState<number | ThemeFixedPageId | null>(null)
    const [pageSections, setPageSections] = useState<Record<number, ThemeSectionInstance[]>>({})
    const [dirtyPageIds, setDirtyPageIds] = useState<number[]>([])
    const [newPageOpen, setNewPageOpen] = useState(false)
    const [deletePageOpen, setDeletePageOpen] = useState(false)
    const [savingPageMeta, setSavingPageMeta] = useState(false)

    // Coleções: valem na hora (sem rascunho). A aberta substitui a página em edição.
    const [collections, setCollections] = useState<StoreCollectionItem[]>([])
    const [currentCollectionId, setCurrentCollectionId] = useState<number | null>(null)
    const [newCollectionOpen, setNewCollectionOpen] = useState(false)
    const [deleteCollectionOpen, setDeleteCollectionOpen] = useState(false)
    const [savingCollection, setSavingCollection] = useState(false)
    const [storeProducts, setStoreProducts] = useState<StoreThemeProduct[]>([])
    const [storeProductsState, setStoreProductsState] = useState<'idle' | 'loading' | 'loaded'>('idle')

    const iframeRef = useRef<HTMLIFrameElement>(null)
    const contentRef = useRef<ThemeContent | null>(null)
    const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    // O que falta gravar: o conteúdo do tema e/ou páginas (o salvamento é um só).
    const themeDirtyRef = useRef(false)
    const pageSectionsRef = useRef<Record<number, ThemeSectionInstance[]>>({})
    const dirtyPagesRef = useRef<Set<number>>(new Set())
    const currentPageRef = useRef<StorePageItem | null>(null)

    const currentPage = typeof currentPageId === 'number' ? pages.find((p) => p.id === currentPageId) ?? null : null
    // Seções que moram no conteúdo do tema: a inicial ou uma das páginas fixas.
    const themePageKey = typeof currentPageId === 'string' ? FIXED_PAGE_KEYS[currentPageId] : HOME_PAGE
    currentPageRef.current = currentPage
    const currentCollection =
        currentCollectionId !== null ? collections.find((c) => c.id === currentCollectionId) ?? null : null

    // A prévia abre no que está em edição: a inicial, `/<slug>`, `/collection/<slug>`
    // ou, nas páginas fixas de produto e coleção, um produto e uma coleção da loja.
    const previewHref = useMemo(() => {
        if (!previewUrl) return ''
        const path = currentCollection
            ? `/collection/${currentCollection.slug}`
            : currentPage
              ? `/${currentPage.slug}`
              : currentPageId === 'product' && storeProducts[0]
                ? `/produtos/${storeProducts[0].id}`
                : currentPageId === 'collection' && collections[0]
                  ? `/collection/${collections[0].slug}`
                  : null
        if (!path) return previewUrl
        try {
            const url = new URL(previewUrl)
            url.pathname = path
            return url.toString()
        } catch {
            return previewUrl
        }
    }, [previewUrl, currentPage, currentCollection, currentPageId, storeProducts, collections])

    const previewOrigin = useMemo(() => {
        try {
            return previewUrl ? new URL(previewUrl).origin : ''
        } catch {
            return ''
        }
    }, [previewUrl])

    const postToPreview = useCallback(
        (message: Record<string, unknown>) => {
            if (!previewOrigin) return
            iframeRef.current?.contentWindow?.postMessage(message, previewOrigin)
        },
        [previewOrigin],
    )

    /** Carrega o estado do editor de um tema e o link da prévia dele. */
    const loadEditor = useCallback(
        async (themeSlug?: string, preloaded?: StoreThemeEditorState) => {
            const state = preloaded ?? (await getStoreThemeEditor(storeConfigId, themeSlug)).result
            if (!state) {
                toast.error('Não foi possível abrir o editor do tema')
                return
            }
            setEditor(state)
            setContent(state.content)
            contentRef.current = state.content
            setHasDraft(state.hasDraft)
            setSaveStatus('saved')
            setSelection({ kind: 'settings' })
            themeDirtyRef.current = false

            const pagesRes = await listStorePages(storeConfigId)
            const loaded = pagesRes.success ? pagesRes.result ?? [] : []
            const working = Object.fromEntries(loaded.map((p) => [p.id, p.draft ?? p.sections]))
            setPages(loaded)
            setPageSections(working)
            pageSectionsRef.current = working
            dirtyPagesRef.current = new Set()
            setDirtyPageIds([])
            setCurrentPageId((current) =>
                typeof current === 'string' || (current && loaded.some((p) => p.id === current)) ? current : null,
            )

            const collectionsRes = await listStoreCollections(storeConfigId)
            const loadedCollections = collectionsRes.success ? collectionsRes.result ?? [] : []
            setCollections(loadedCollections)
            setCurrentCollectionId((current) =>
                current && loadedCollections.some((c) => c.id === current) ? current : null,
            )

            const preview = await createStoreThemePreview(storeConfigId, state.themeSlug)
            if (preview.success && preview.result?.url) setPreviewUrl(preview.result.url)
        },
        [storeConfigId],
    )

    useEffect(() => {
        if (!storeConfigId) return
        let cancelled = false
        ;(async () => {
            setLoading(true)
            try {
                if (requestedTheme) {
                    // Idempotente: tema que já tem registro só abre, sem recriar nada.
                    const res = await prepareStoreThemeSwitch(storeConfigId, requestedTheme, requestedCatalog)
                    if (cancelled) return
                    if (res.success && res.result) {
                        await loadEditor(requestedTheme, res.result)
                        if (res.result.activeSlug !== requestedTheme) {
                            toast.info('Edite o tema novo e publique para ativá-lo na loja.')
                        }
                    } else {
                        toast.error(res.error || 'Não foi possível abrir o tema')
                        await loadEditor()
                    }
                } else {
                    await loadEditor()
                }
                // Tudo pela API de temas: quem tem só o recurso `store-themes` não
                // esbarra em 403 de widgets ou categorias.
                const optionsRes = await getStoreThemeFieldOptions(storeConfigId).catch(() => null)
                if (cancelled) return
                if (optionsRes?.success && optionsRes.result) setFieldOptions(optionsRes.result)
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [storeConfigId, loadEditor, requestedTheme, requestedCatalog])

    // Mensagens da prévia: pronta (recebe o conteúdo atual) e clique numa seção.
    useEffect(() => {
        const handle = (event: MessageEvent) => {
            if (!previewOrigin || event.origin !== previewOrigin) return
            if (event.source !== iframeRef.current?.contentWindow) return
            if (event.data?.type === 'dex-theme-ready' && contentRef.current) {
                postToPreview({ type: 'dex-theme-content', content: contentRef.current })
                const page = currentPageRef.current
                if (page) {
                    postToPreview({ type: 'dex-theme-page', slug: page.slug, sections: pageSectionsRef.current[page.id] ?? [] })
                }
            } else if (event.data?.type === 'dex-theme-section-click' && typeof event.data.sectionId === 'string') {
                // Clicou numa seção da prévia: fecha a prévia e abre os campos dela.
                setSelection({ kind: 'section', id: event.data.sectionId })
                setPreviewOpen(false)
            } else if (
                event.data?.type === 'dex-theme-part-click' &&
                (THEME_PART_KEYS as string[]).includes(event.data.part)
            ) {
                // "Editar cabeçalho" (ou rodapé) na prévia: fecha a prévia e abre a parte.
                setCurrentCollectionId(null)
                setSelection({ kind: 'part', part: event.data.part as ThemePartKey })
                setPreviewOpen(false)
            }
        }
        window.addEventListener('message', handle)
        return () => window.removeEventListener('message', handle)
    }, [previewOrigin, postToPreview])

    // Trocar de parte, seção ou página abre o painel de campos no topo.
    const fieldsPanelRef = useRef<HTMLElement>(null)
    useEffect(() => {
        fieldsPanelRef.current?.scrollTo({ top: 0 })
    }, [selection, currentPageId, currentCollectionId])

    // A seção selecionada no painel é destacada na prévia.
    useEffect(() => {
        postToPreview({ type: 'dex-theme-select', sectionId: selection.kind === 'section' ? selection.id : null })
    }, [selection, postToPreview])

    /** Grava o rascunho do tema e das páginas que mudaram desde o último salvamento. */
    const saveDraft = useCallback(async () => {
        if (!editor || !contentRef.current) return
        setSaveStatus('saving')
        const errors: string[] = []

        if (themeDirtyRef.current) {
            themeDirtyRef.current = false
            const res = await saveStoreThemeDraft(storeConfigId, editor.themeSlug, contentRef.current)
            if (!res.success) {
                themeDirtyRef.current = true
                errors.push(res.error || 'Não foi possível salvar o rascunho do tema')
            }
        }

        for (const pageId of [...dirtyPagesRef.current]) {
            dirtyPagesRef.current.delete(pageId)
            const res = await saveStorePageDraft(
                storeConfigId,
                pageId,
                editor.themeSlug,
                pageSectionsRef.current[pageId] ?? [],
            )
            if (!res.success) {
                dirtyPagesRef.current.add(pageId)
                errors.push(res.error || 'Não foi possível salvar a página')
            }
        }
        setDirtyPageIds([...dirtyPagesRef.current])

        if (errors.length === 0) {
            setSaveStatus((status) => (status === 'saving' ? 'saved' : status))
            setHasDraft(true)
            // Dados das seções (produtos da vitrine) vêm do servidor: buscar de novo.
            postToPreview({ type: 'dex-theme-refresh' })
        } else {
            setSaveStatus('error')
            toast.error(errors[0])
        }
    }, [editor, storeConfigId, postToPreview])

    const scheduleSave = useCallback(() => {
        setSaveStatus('pending')
        if (saveTimer.current) clearTimeout(saveTimer.current)
        saveTimer.current = setTimeout(saveDraft, AUTOSAVE_DELAY_MS)
    }, [saveDraft])

    const updateContent = useCallback(
        (next: ThemeContent) => {
            setContent(next)
            contentRef.current = next
            themeDirtyRef.current = true
            postToPreview({ type: 'dex-theme-content', content: next })
            scheduleSave()
        },
        [postToPreview, scheduleSave],
    )

    useEffect(
        () => () => {
            if (saveTimer.current) clearTimeout(saveTimer.current)
        },
        [],
    )

    const sections = currentPage ? pageSections[currentPage.id] ?? [] : content?.pages[themePageKey] ?? []
    const selectedSection =
        selection.kind === 'section' ? sections.find((s) => s.id === selection.id) ?? null : null
    // A página inicial e a barra lateral não têm configurações próprias: abrem no "Estilo".
    const currentPart: ThemePartKey | null = currentCollection
        ? null
        : selection.kind === 'part'
          ? selection.part
          : selection.kind === 'settings' && !currentPage
            ? 'style'
            : null

    const setSections = (next: ThemeSectionInstance[]) => {
        if (currentPage) {
            const working = { ...pageSectionsRef.current, [currentPage.id]: next }
            pageSectionsRef.current = working
            setPageSections(working)
            dirtyPagesRef.current.add(currentPage.id)
            setDirtyPageIds([...dirtyPagesRef.current])
            postToPreview({ type: 'dex-theme-page', slug: currentPage.slug, sections: next })
            scheduleSave()
            return
        }
        if (!content) return
        updateContent({ ...content, pages: { ...content.pages, [themePageKey]: next } })
    }

    const selectPage = (pageId: number | ThemeFixedPageId | null) => {
        setCurrentPageId(pageId)
        setCurrentCollectionId(null)
        setSelection({ kind: 'settings' })
    }

    const selectPart = (part: ThemePartKey) => {
        setCurrentCollectionId(null)
        setSelection({ kind: 'part', part })
    }

    const selectCollection = (collectionId: number) => {
        setCurrentCollectionId(collectionId)
        setSelection({ kind: 'settings' })
    }

    // Produtos da loja só quando precisa (lista que pode ser longa): coleção manual
    // aberta, ou a página fixa de produto, cuja prévia abre no primeiro produto.
    useEffect(() => {
        if ((currentCollection?.kind !== 'MANUAL' && currentPageId !== 'product') || storeProductsState !== 'idle') return
        setStoreProductsState('loading')
        listStoreThemeProducts(storeConfigId).then((res) => {
            setStoreProducts(res.success ? res.result ?? [] : [])
            setStoreProductsState('loaded')
        })
    }, [currentCollection?.kind, currentPageId, storeProductsState, storeConfigId])

    /** A lista do campo "Coleção" da vitrine de produtos acompanha as coleções. */
    const applyCollections = (next: StoreCollectionItem[]) => {
        setCollections(next)
        setFieldOptions((current) => ({ ...current, collections: next.map((c) => ({ id: c.id, name: c.title })) }))
    }

    const handleCreateCollection = async (data: { title: string; kind: StoreCollectionKind; categoryIds: number[] }) => {
        setBusy(true)
        try {
            const res = await createStoreCollection(storeConfigId, {
                title: data.title,
                kind: data.kind,
                ...(data.kind === 'CATEGORY' ? { categoryIds: data.categoryIds } : {}),
            })
            if (!res.success || !res.result) {
                toast.error(res.error || 'Não foi possível criar a coleção')
                return
            }
            const created = res.result
            applyCollections([...collections, created])
            setNewCollectionOpen(false)
            selectCollection(created.id)
            toast.success('Coleção criada. Ela já vale na loja.')
        } finally {
            setBusy(false)
        }
    }

    const handleSaveCollection = async (meta: StoreCollectionMeta, productIds?: number[]) => {
        if (!currentCollection) return
        setSavingCollection(true)
        try {
            const res = await updateStoreCollection(storeConfigId, currentCollection.id, meta)
            if (!res.success || !res.result) {
                toast.error(res.error || 'Não foi possível salvar a coleção')
                return
            }
            let updated = res.result
            if (productIds) {
                const productsRes = await setStoreCollectionProducts(storeConfigId, currentCollection.id, productIds)
                if (!productsRes.success || !productsRes.result) {
                    toast.error(productsRes.error || 'Não foi possível salvar os produtos da coleção')
                    applyCollections(collections.map((c) => (c.id === updated.id ? updated : c)))
                    return
                }
                updated = productsRes.result
            }
            applyCollections(collections.map((c) => (c.id === updated.id ? updated : c)))
            postToPreview({ type: 'dex-theme-refresh' })
            toast.success('Coleção salva')
        } finally {
            setSavingCollection(false)
        }
    }

    const handleReorderCollections = async (next: StoreCollectionItem[]) => {
        const previous = collections
        applyCollections(next)
        const res = await reorderStoreCollections(storeConfigId, next.map((c) => c.id))
        if (!res.success) {
            applyCollections(previous)
            toast.error(res.error || 'Não foi possível reordenar as coleções')
        }
    }

    const handleDeleteCollection = async () => {
        if (!currentCollection) return
        setBusy(true)
        try {
            const res = await removeStoreCollection(storeConfigId, currentCollection.id)
            if (res.success) {
                applyCollections(collections.filter((c) => c.id !== currentCollection.id))
                setDeleteCollectionOpen(false)
                selectPage(null)
                toast.success('Coleção removida')
            } else {
                toast.error(res.error || 'Não foi possível remover a coleção')
            }
        } finally {
            setBusy(false)
        }
    }

    const handleCreatePage = async (templateKey: string, title: string) => {
        if (!editor) return
        setBusy(true)
        try {
            const res = await createStorePage(storeConfigId, {
                themeSlug: editor.themeSlug,
                templateKey,
                title: title || undefined,
            })
            if (!res.success || !res.result) {
                toast.error(res.error || 'Não foi possível criar a página')
                return
            }
            const page = res.result
            setPages((current) => [...current, page])
            const working = { ...pageSectionsRef.current, [page.id]: page.draft ?? page.sections }
            pageSectionsRef.current = working
            setPageSections(working)
            setHasDraft(true)
            setNewPageOpen(false)
            setCurrentPageId(page.id)
            setSelection({ kind: 'settings' })
            toast.success('Página criada. Ela entra na loja quando você publicar.')
        } finally {
            setBusy(false)
        }
    }

    const handleSavePageMeta = async (meta: StorePageMeta) => {
        if (!currentPage) return
        setSavingPageMeta(true)
        try {
            const res = await updateStorePage(storeConfigId, currentPage.id, meta)
            if (res.success && res.result) {
                const updated = res.result
                setPages((current) => current.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)))
                toast.success('Página atualizada')
            } else {
                toast.error(res.error || 'Não foi possível salvar a página')
            }
        } finally {
            setSavingPageMeta(false)
        }
    }

    const handleReorderPages = async (next: StorePageItem[]) => {
        const previous = pages
        setPages(next)
        const res = await reorderStorePages(storeConfigId, next.map((p) => p.id))
        if (!res.success) {
            setPages(previous)
            toast.error(res.error || 'Não foi possível reordenar as páginas')
        }
    }

    const handleDeletePage = async () => {
        if (!currentPage) return
        setBusy(true)
        try {
            const res = await removeStorePage(storeConfigId, currentPage.id)
            if (res.success) {
                dirtyPagesRef.current.delete(currentPage.id)
                setDirtyPageIds([...dirtyPagesRef.current])
                setPages((current) => current.filter((p) => p.id !== currentPage.id))
                setDeletePageOpen(false)
                selectPage(null)
                toast.success('Página removida')
            } else {
                toast.error(res.error || 'Não foi possível remover a página')
            }
        } finally {
            setBusy(false)
        }
    }

    const options: ThemeFieldOptions = useMemo(
        () => ({
            ...fieldOptions,
            uploadMedia: async (file: File) => {
                const res = await uploadStoreThemeMedia(storeConfigId, file)
                const url = res.result?.url
                if (!res.success || !url) {
                    toast.error(res.error || 'Não foi possível enviar a imagem')
                    return null
                }
                return url
            },
        }),
        [fieldOptions, storeConfigId],
    )

    /** Grava o que estiver pendente antes de publicar ou trocar de tema. */
    const flushPending = async () => {
        if (saveTimer.current) {
            clearTimeout(saveTimer.current)
            saveTimer.current = null
            await saveDraft()
        }
    }

    const handlePublish = async () => {
        if (!editor) return
        setBusy(true)
        try {
            await flushPending()
            const res = await publishStoreTheme(storeConfigId, editor.themeSlug)
            if (res.success) {
                toast.success('Tema publicado. A loja já mostra as alterações.')
                setPublishOpen(false)
                await loadEditor(editor.themeSlug)
            } else {
                toast.error(res.error || 'Não foi possível publicar')
            }
        } finally {
            setBusy(false)
        }
    }

    const handleDiscard = async () => {
        if (!editor) return
        setBusy(true)
        try {
            if (saveTimer.current) clearTimeout(saveTimer.current)
            const res = await discardStoreThemeDraft(storeConfigId, editor.themeSlug)
            if (res.success) {
                toast.success('Alterações descartadas')
                setDiscardOpen(false)
                await loadEditor(editor.themeSlug)
            } else {
                toast.error(res.error || 'Não foi possível descartar')
            }
        } finally {
            setBusy(false)
        }
    }

    const openVersions = async () => {
        if (!editor) return
        setVersionsOpen(true)
        const res = await listStoreThemeVersions(storeConfigId, editor.themeSlug)
        setVersions(res.success ? res.result ?? [] : [])
    }

    const handleRestore = async (versionId: number) => {
        if (!editor) return
        setBusy(true)
        try {
            await flushPending()
            const res = await restoreStoreThemeVersion(storeConfigId, versionId)
            if (res.success) {
                toast.success('Versão trazida para o rascunho. Publique para colocar no ar.')
                setVersionsOpen(false)
                await loadEditor(editor.themeSlug)
            } else {
                toast.error(res.error || 'Não foi possível restaurar a versão')
            }
        } finally {
            setBusy(false)
        }
    }

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <span className="loading loading-spinner loading-lg" />
            </div>
        )
    }

    if (!editor || !content) {
        return (
            <div className="p-6">
                <Link to="/commerce/themes" className="btn btn-ghost btn-sm">
                    <MdArrowBack /> Voltar para os temas
                </Link>
            </div>
        )
    }

    const store = editor.store

    const manifest = editor.manifest
    const isActiveTheme = editor.activeSlug === editor.themeSlug
    const statusLabel: Record<SaveStatus, string> = {
        saved: hasDraft ? 'Rascunho salvo' : 'Sem alterações',
        pending: 'Alterações não salvas',
        saving: 'Salvando...',
        error: 'Erro ao salvar',
    }

    return (
        <div className="flex h-[calc(100vh-5rem)] flex-col gap-3 p-3">
            {/* Barra de ações */}
            <div className="flex flex-wrap items-center gap-3 rounded-xl bg-base-100 px-4 py-3 shadow-sm">
                <Link to="/commerce/themes" className="btn btn-ghost btn-sm btn-square" title="Voltar">
                    <MdArrowBack className="text-lg" />
                </Link>
                <div className="flex min-w-0 flex-col">
                    <span className="truncate font-semibold">{store.name}</span>
                    <span className="text-xs text-base-content/60">
                        Tema {manifest.name}
                        {isActiveTheme ? ' (ativo)' : ' (não publicado)'}
                    </span>
                </div>

                <span
                    className={`badge badge-sm ${saveStatus === 'error' ? 'badge-error' : saveStatus === 'saved' ? 'badge-ghost' : 'badge-warning'}`}
                >
                    {statusLabel[saveStatus]}
                </span>

                <div className="ml-auto flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        disabled={!previewUrl}
                        onClick={() => setPreviewOpen(true)}
                    >
                        <MdVisibility /> Prévia
                    </button>
                    <Link to={`/commerce/themes?store=${storeConfigId}`} className="btn btn-sm btn-ghost">
                        <MdStyle /> Temas
                    </Link>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={openVersions}>
                        <MdHistory /> Versões
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={!hasDraft || busy}
                        onClick={() => setDiscardOpen(true)}
                    >
                        <MdUndo /> Descartar
                    </button>
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={busy || saveStatus === 'saving' || (!hasDraft && saveStatus === 'saved')}
                        onClick={() => setPublishOpen(true)}
                    >
                        <MdPublish /> {isActiveTheme ? 'Publicar' : 'Publicar e ativar'}
                    </button>
                </div>
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[300px_minmax(0,1fr)]">
                {/* Seções */}
                <aside className="flex flex-col gap-5 overflow-y-auto rounded-xl bg-base-100 p-4 shadow-sm">
                    <PartList currentPart={currentPart} onSelect={selectPart} />
                    <div className="border-t border-base-200 pt-4">
                    <PageList
                        pages={pages}
                        // Com uma coleção aberta, nenhuma página fica marcada (nem a inicial).
                        currentPageId={currentCollection ? -1 : currentPageId}
                        dirtyPageIds={dirtyPageIds}
                        onSelect={selectPage}
                        onReorder={handleReorderPages}
                        onCreate={() => setNewPageOpen(true)}
                    />
                    </div>
                    <div className="border-t border-base-200 pt-4">
                        <CollectionList
                            collections={collections}
                            currentCollectionId={currentCollectionId}
                            onSelect={selectCollection}
                            onReorder={handleReorderCollections}
                            onCreate={() => setNewCollectionOpen(true)}
                        />
                    </div>
                    {!currentCollection && (
                        <div className="border-t border-base-200 pt-4">
                            <SectionList
                                manifest={manifest}
                                page={currentPage ? 'page' : themePageKey}
                                sections={sections}
                                selection={selection}
                                onSelect={setSelection}
                                onChange={setSections}
                                settingsLabel={currentPage ? 'Título, endereço e SEO' : undefined}
                            />
                            {themePageKey === SIDEBAR_PAGE &&
                                !currentPage &&
                                !THEME_SIDEBAR_LAYOUTS.includes(String(content.parts.frame.layout ?? '')) && (
                                    <p className="mt-3 rounded-lg bg-warning/10 p-3 text-xs text-base-content/70">
                                        A disposição atual não tem barra lateral. Escolha uma com barra lateral em
                                        "Moldura da página" para estas seções aparecerem na loja.
                                    </p>
                                )}
                            {(themePageKey === 'product' || themePageKey === 'collection') && !currentPage && (
                                <p className="mt-3 rounded-lg bg-base-200 p-3 text-xs text-base-content/70">
                                    {themePageKey === 'product'
                                        ? 'Estas seções aparecem abaixo do detalhe, em todo produto da loja.'
                                        : 'Estas seções aparecem abaixo da grade de produtos, em toda coleção da loja.'}
                                </p>
                            )}
                        </div>
                    )}
                </aside>

                {/* Campos */}
                <aside ref={fieldsPanelRef} className="overflow-y-auto rounded-xl bg-base-100 p-6 shadow-sm">
                    <div className="mx-auto max-w-5xl">
                    {currentCollection ? (
                        <CollectionSettings
                            collection={currentCollection}
                            categories={fieldOptions.categories}
                            products={storeProducts}
                            productsLoading={storeProductsState === 'loading'}
                            options={options}
                            saving={savingCollection}
                            onSave={handleSaveCollection}
                            onDelete={() => setDeleteCollectionOpen(true)}
                        />
                    ) : currentPart ? (
                        <div className="flex flex-col gap-5">
                            <div className="flex flex-col gap-1">
                                <h3 className="text-base font-semibold">{THEME_PARTS[currentPart].label}</h3>
                                {THEME_PARTS[currentPart].help && (
                                    <p className="text-sm text-base-content/60">{THEME_PARTS[currentPart].help}</p>
                                )}
                            </div>
                            <ThemeFieldGrid
                                idPrefix={`part-${currentPart}`}
                                fields={themePartFields(manifest, currentPart)}
                                values={content.parts[currentPart]}
                                options={options}
                                onChange={(key, value) =>
                                    updateContent({
                                        ...content,
                                        parts: {
                                            ...content.parts,
                                            [currentPart]: { ...content.parts[currentPart], [key]: value },
                                        },
                                    })
                                }
                            />
                        </div>
                    ) : selection.kind === 'settings' && currentPage ? (
                        <PageSettings
                            page={currentPage}
                            options={options}
                            saving={savingPageMeta}
                            onSave={handleSavePageMeta}
                            onDelete={() => setDeletePageOpen(true)}
                        />
                    ) : selectedSection ? (
                        <div className="flex flex-col gap-5">
                            <div className="flex items-center justify-between gap-2">
                                <h3 className="text-base font-semibold">
                                    {manifest.sections[selectedSection.type]?.label ?? selectedSection.type}
                                </h3>
                                {selectedSection.hidden && <span className="badge badge-ghost badge-sm">Escondida</span>}
                            </div>
                            <ThemeFieldGrid
                                idPrefix={selectedSection.id}
                                fields={manifest.sections[selectedSection.type]?.settings ?? {}}
                                values={selectedSection.settings}
                                options={options}
                                onChange={(key, value) =>
                                    setSections(
                                        sections.map((s) =>
                                            s.id === selectedSection.id
                                                ? { ...s, settings: { ...s.settings, [key]: value } }
                                                : s,
                                        ),
                                    )
                                }
                            />
                        </div>
                    ) : (
                        <p className="text-sm text-base-content/60">Escolha uma seção na lista ou na prévia.</p>
                    )}
                    </div>
                </aside>
            </div>

            {/* Prévia em tela quase cheia. O iframe só existe com o modal aberto; ao
                carregar, avisa que está pronto e recebe o conteúdo em edição. */}
            {previewOpen && previewHref && (
                <dialog className="modal modal-open z-50">
                    <div className="modal-box flex h-[92vh] w-[96vw] !max-w-none flex-col gap-3 !p-3">
                        <div className="flex flex-wrap items-center gap-2 px-1">
                            <span className="font-semibold">
                                Prévia de {store.name}
                                {currentCollection ? ` / ${currentCollection.title}` : currentPage ? ` / ${currentPage.title}` : ''}
                            </span>
                            <span className="text-xs text-base-content/60">
                                Clique numa seção para editá-la. A loja só muda ao publicar.
                            </span>
                            <div className="ml-auto flex items-center gap-2">
                                <div className="join">
                                    <button
                                        type="button"
                                        className={`btn btn-sm join-item ${device === 'desktop' ? 'btn-active' : ''}`}
                                        onClick={() => setDevice('desktop')}
                                        title="Computador"
                                    >
                                        <MdDesktopWindows />
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn btn-sm join-item ${device === 'mobile' ? 'btn-active' : ''}`}
                                        onClick={() => setDevice('mobile')}
                                        title="Celular"
                                    >
                                        <MdPhoneIphone />
                                    </button>
                                </div>
                                <a href={previewHref} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-ghost">
                                    <MdOpenInNew /> Outra aba
                                </a>
                                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPreviewOpen(false)} title="Fechar">
                                    <MdClose className="text-lg" />
                                </button>
                            </div>
                        </div>
                        <div className="flex min-h-0 flex-1 justify-center overflow-auto rounded-lg bg-base-200 p-2">
                            <iframe
                                ref={iframeRef}
                                src={previewHref}
                                title="Prévia da loja"
                                className={`h-full rounded-lg border border-base-300 bg-white shadow-sm transition-[width] ${device === 'mobile' ? 'w-[390px]' : 'w-full'}`}
                            />
                        </div>
                    </div>
                    <div className="modal-backdrop" onClick={() => setPreviewOpen(false)} />
                </dialog>
            )}

            <ConfirmModal
                isOpen={publishOpen}
                onClose={() => setPublishOpen(false)}
                onConfirm={handlePublish}
                loading={busy}
                title={isActiveTheme ? 'Publicar alterações' : 'Publicar e ativar o tema'}
                message={
                    isActiveTheme
                        ? 'A loja passa a mostrar o rascunho agora. A versão atual fica no histórico.'
                        : `A loja passa a usar o tema ${manifest.name} agora. O tema anterior fica guardado.`
                }
                confirmButtonText="Publicar"
                confirmButtonClass="btn-primary"
            />

            <NewPageDialog
                open={newPageOpen}
                busy={busy}
                onClose={() => setNewPageOpen(false)}
                onCreate={handleCreatePage}
            />

            <ConfirmModal
                isOpen={deletePageOpen}
                onClose={() => setDeletePageOpen(false)}
                onConfirm={handleDeletePage}
                loading={busy}
                title="Remover página"
                message={`A página "${currentPage?.title ?? ''}" sai da loja e do rodapé agora. Não dá para desfazer.`}
                confirmButtonText="Remover"
            />

            <NewCollectionDialog
                open={newCollectionOpen}
                busy={busy}
                categories={fieldOptions.categories}
                onClose={() => setNewCollectionOpen(false)}
                onCreate={handleCreateCollection}
            />

            <ConfirmModal
                isOpen={deleteCollectionOpen}
                onClose={() => setDeleteCollectionOpen(false)}
                onConfirm={handleDeleteCollection}
                loading={busy}
                title="Remover coleção"
                message={`A coleção "${currentCollection?.title ?? ''}" sai da loja agora, e a vitrine de produtos que a usa fica vazia. Não dá para desfazer.`}
                confirmButtonText="Remover"
            />

            <ConfirmModal
                isOpen={discardOpen}
                onClose={() => setDiscardOpen(false)}
                onConfirm={handleDiscard}
                loading={busy}
                title="Descartar alterações"
                message="O tema e as páginas voltam a ser iguais ao que está publicado. Páginas novas, ainda não publicadas, continuam (para tirá-las, use Remover página). Não dá para desfazer."
                confirmButtonText="Descartar"
            />

            <ThemeDialog open={versionsOpen} title="Versões publicadas" onClose={() => setVersionsOpen(false)}>
                {versions.length === 0 ? (
                    <p className="text-sm text-base-content/60">Este tema ainda não foi publicado.</p>
                ) : (
                    <ul className="flex flex-col divide-y divide-base-200">
                        {versions.map((version, index) => (
                            <li key={version.id} className="flex items-center justify-between gap-3 py-3">
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium">{formatDateTime(version.createdAt)}</span>
                                    {index === 0 && <span className="text-xs text-success">No ar</span>}
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline"
                                    disabled={busy}
                                    onClick={() => handleRestore(version.id)}
                                >
                                    Trazer para o rascunho
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </ThemeDialog>

        </div>
    )
}
